/**
 * Hook & Context pour les permissions RBAC V2.
 * 
 * Charge GET /api/auth/me/permissions au login (ou au mount) et expose :
 * - permissions: Set<string> des permissions effectives du user
 * - hasPermission(perm: string): boolean
 * - hasAnyPermission(perms: string[]): boolean
 * - isAdmin: boolean
 * - loading: boolean
 * - refresh(): re-fetch
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(new Set());
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    try {
      // SEC-003 : cookie HttpOnly envoyé via withCredentials, plus de localStorage
      const res = await axios.get(`${API}/auth/me/permissions`);
      setPermissions(new Set(res.data.permissions || []));
      setIsAdmin(!!res.data.isAdmin);
    } catch (err) {
      console.warn('[usePermissions] Failed to fetch permissions:', err?.response?.data || err.message);
      setPermissions(new Set());
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // RBAC fix 15/06/2026 — propage les changements de permissions SANS re-login :
  // re-fetch au focus de l'onglet + toutes les 90s tant que l'utilisateur est connecté.
  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => fetchPermissions();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(fetchPermissions, 90000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [user, fetchPermissions]);

  const hasPermission = useCallback(
    (perm) => {
      if (!perm) return true;
      if (isAdmin) return true;
      return permissions.has(perm);
    },
    [permissions, isAdmin]
  );

  const hasAnyPermission = useCallback(
    (perms = []) => {
      if (!perms.length) return true;
      if (isAdmin) return true;
      return perms.some((p) => permissions.has(p));
    },
    [permissions, isAdmin]
  );

  const value = useMemo(
    () => ({
      permissions,
      isAdmin,
      loading,
      hasPermission,
      hasAnyPermission,
      refresh: fetchPermissions,
    }),
    [permissions, isAdmin, loading, hasPermission, hasAnyPermission, fetchPermissions]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Fallback gracieux : pas dans le provider → renvoie un objet vide non bloquant
    return {
      permissions: new Set(),
      isAdmin: false,
      loading: false,
      hasPermission: () => false,
      hasAnyPermission: () => false,
      refresh: () => {},
    };
  }
  return ctx;
};

/**
 * Composant <Can> — Affiche ses enfants si la permission est accordée.
 * 
 * <Can permission="finance.cockpit.view">
 *   <Link to="/admin/finance/cockpit">Cockpit CFO</Link>
 * </Can>
 * 
 * <Can anyPermission={['finance.audit.view','admin']} fallback={<NotAllowed/>}>
 *   ...
 * </Can>
 */
export const Can = ({ permission, anyPermission, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission, loading } = usePermissions();
  if (loading) return null;
  const allowed = anyPermission
    ? hasAnyPermission(anyPermission)
    : permission
    ? hasPermission(permission)
    : true;
  return allowed ? <>{children}</> : <>{fallback}</>;
};

export default usePermissions;
